import { acquireRelease } from '../../Effect/dist/effect-Effect.esm.js';
import { dual, pipe, identity } from '../../Function/dist/effect-Function.esm.js';
import { empty, add, forEach as forEach$1, remove } from '../../HashSet/dist/effect-HashSet.esm.js';
import { some, none } from '../../Option/dist/effect-Option.esm.js';
import { every } from '../../ReadonlyArray/dist/effect-ReadonlyArray.esm.js';
import { flatMap, map, effect, retry, withSTMRuntime, interruptAs, succeed } from './core.esm.js';
import { OP_SLIDING_STRATEGY, OP_DROPPING_STRATEGY, OP_BACKPRESSURE_STRATEGY } from './opCodes/strategy.esm.js';
import { all, tap, unit, forEach } from './stm.esm.js';
import { shutdown, BackPressure, Dropping, Sliding, TEnqueueTypeId, tEnqueueVariance, TDequeueTypeId, tDequeueVariance } from './tQueue.esm.js';
import { make, get, set, unsafeGet, TRefImpl, unsafeSet } from './tRef.esm.js';

/** @internal */
const TPubSubSymbolKey = "effect/TPubSub";

/** @internal */
const TPubSubTypeId = /*#__PURE__*/Symbol.for(TPubSubSymbolKey);

/** @internal */

/** @internal */
const makeNode = (head, subscribers, tail) => ({
  head,
  subscribers,
  tail
});

/** @internal */
class TPubSubImpl {
  [TPubSubTypeId] = TPubSubTypeId;
  [TEnqueueTypeId] = tEnqueueVariance;
  constructor(pubsubSize, publisherHead, publisherTail, requestedCapacity, strategy, subscriberCount, subscribers) {
    this.pubsubSize = pubsubSize;
    this.publisherHead = publisherHead;
    this.publisherTail = publisherTail;
    this.requestedCapacity = requestedCapacity;
    this.strategy = strategy;
    this.subscriberCount = subscriberCount;
    this.subscribers = subscribers;
  }
  isShutdown = effect(journal => {
    const currentPublisherTail = unsafeGet(this.publisherTail, journal);
    return currentPublisherTail === undefined;
  });
  awaitShutdown = flatMap(this.isShutdown, isShutdown => isShutdown ? unit : retry);
  capacity() {
    return this.requestedCapacity;
  }
  size = withSTMRuntime(runtime => {
    const currentPublisherTail = unsafeGet(this.publisherTail, runtime.journal);
    if (currentPublisherTail === undefined) {
      return interruptAs(runtime.fiberId);
    }
    return succeed(unsafeGet(this.pubsubSize, runtime.journal));
  });
  isEmpty = map(this.size, size => size === 0);
  isFull = map(this.size, size => size === this.capacity());
  offer(value) {
    return withSTMRuntime(runtime => {
      const currentPublisherTail = unsafeGet(this.publisherTail, runtime.journal);
      if (currentPublisherTail === undefined) {
        return interruptAs(runtime.fiberId);
      }
      const currentSubscriberCount = unsafeGet(this.subscriberCount, runtime.journal);
      if (currentSubscriberCount === 0) {
        return succeed(true);
      }
      const currentPubSubSize = unsafeGet(this.pubsubSize, runtime.journal);
      if (currentPubSubSize < this.requestedCapacity) {
        const updatedPublisherTail = new TRefImpl(void 0);
        const updatedNode = makeNode(value, currentSubscriberCount, updatedPublisherTail);
        unsafeSet(currentPublisherTail, updatedNode, runtime.journal);
        unsafeSet(this.publisherTail, updatedPublisherTail, runtime.journal);
        unsafeSet(this.pubsubSize, currentPubSubSize + 1, runtime.journal);
        return succeed(true);
      }
      switch (this.strategy._tag) {
        case OP_BACKPRESSURE_STRATEGY:
          {
            return retry;
          }
        case OP_DROPPING_STRATEGY:
          {
            return succeed(false);
          }
        case OP_SLIDING_STRATEGY:
          {
            if (this.requestedCapacity > 0) {
              let currentPublisherHead = unsafeGet(this.publisherHead, runtime.journal);
              let loop = true;
              while (loop) {
                const node = unsafeGet(currentPublisherHead, runtime.journal);
                if (node === undefined) {
                  return retry;
                }
                const head = node.head;
                const tail = node.tail;
                if (head !== undefined) {
                  const updatedNode = makeNode(void 0, node.subscribers, node.tail);
                  unsafeSet(currentPublisherHead, updatedNode, runtime.journal);
                  unsafeSet(this.publisherHead, tail, runtime.journal);
                  loop = false;
                } else {
                  currentPublisherHead = tail;
                }
              }
            }
            const updatedPublisherTail = new TRefImpl(void 0);
            const updatedNode = makeNode(value, currentSubscriberCount, updatedPublisherTail);
            unsafeSet(currentPublisherTail, updatedNode, runtime.journal);
            unsafeSet(this.publisherTail, updatedPublisherTail, runtime.journal);
            return succeed(true);
          }
      }
    });
  }
  offerAll(iterable) {
    return map(forEach(iterable, a => this.offer(a)), every(identity));
  }
  shutdown = effect(journal => {
    const currentPublisherTail = unsafeGet(this.publisherTail, journal);
    if (currentPublisherTail !== undefined) {
      unsafeSet(this.publisherTail, void 0, journal);
      const currentSubscribers = unsafeGet(this.subscribers, journal);
      forEach$1(currentSubscribers, subscriber => {
        unsafeSet(subscriber, void 0, journal);
      });
      unsafeSet(this.subscribers, empty(), journal);
    }
  });
}

/** @internal */
class TPubSubSubscriptionImpl {
  [TPubSubTypeId] = TPubSubTypeId;
  [TDequeueTypeId] = tDequeueVariance;
  constructor(pubsubSize, publisherHead, requestedCapacity, subscriberHead, subscriberCount, subscribers) {
    this.pubsubSize = pubsubSize;
    this.publisherHead = publisherHead;
    this.requestedCapacity = requestedCapacity;
    this.subscriberHead = subscriberHead;
    this.subscriberCount = subscriberCount;
    this.subscribers = subscribers;
  }
  isShutdown = effect(journal => {
    const currentSubscriberHead = unsafeGet(this.subscriberHead, journal);
    return currentSubscriberHead === undefined;
  });
  awaitShutdown = flatMap(this.isShutdown, isShutdown => isShutdown ? unit : retry);
  capacity() {
    return this.requestedCapacity;
  }
  size = withSTMRuntime(runtime => {
    let currentSubscriberHead = unsafeGet(this.subscriberHead, runtime.journal);
    if (currentSubscriberHead === undefined) {
      return interruptAs(runtime.fiberId);
    }
    let loop = true;
    let size = 0;
    while (loop) {
      const node = unsafeGet(currentSubscriberHead, runtime.journal);
      if (node === undefined) {
        loop = false;
      } else {
        const head = node.head;
        const tail = node.tail;
        if (head !== undefined) {
          size = size + 1;
          if (size >= Number.MAX_SAFE_INTEGER) {
            loop = false;
          }
        }
        currentSubscriberHead = tail;
      }
    }
    return succeed(size);
  });
  isEmpty = map(this.size, size => size === 0);
  isFull = map(this.size, size => size === this.capacity());
  peek = withSTMRuntime(runtime => {
    let currentSubscriberHead = unsafeGet(this.subscriberHead, runtime.journal);
    if (currentSubscriberHead === undefined) {
      return interruptAs(runtime.fiberId);
    }
    let value = undefined;
    let loop = true;
    while (loop) {
      const node = unsafeGet(currentSubscriberHead, runtime.journal);
      if (node === undefined) {
        return retry;
      }
      const head = node.head;
      const tail = node.tail;
      if (head !== undefined) {
        value = head;
        loop = false;
      } else {
        currentSubscriberHead = tail;
      }
    }
    return succeed(value);
  });
  peekOption = withSTMRuntime(runtime => {
    let currentSubscriberHead = unsafeGet(this.subscriberHead, runtime.journal);
    if (currentSubscriberHead === undefined) {
      return interruptAs(runtime.fiberId);
    }
    let value = none();
    let loop = true;
    while (loop) {
      const node = unsafeGet(currentSubscriberHead, runtime.journal);
      if (node === undefined) {
        value = none();
        loop = false;
      } else {
        const head = node.head;
        const tail = node.tail;
        if (head !== undefined) {
          value = some(head);
          loop = false;
        } else {
          currentSubscriberHead = tail;
        }
      }
    }
    return succeed(value);
  });
  shutdown = effect(journal => {
    let currentSubscriberHead = unsafeGet(this.subscriberHead, journal);
    if (currentSubscriberHead !== undefined) {
      unsafeSet(this.subscriberHead, void 0, journal);
      let loop = true;
      while (loop) {
        const node = unsafeGet(currentSubscriberHead, journal);
        if (node === undefined) {
          loop = false;
        } else {
          const head = node.head;
          const tail = node.tail;
          if (head !== undefined) {
            const subscribers = node.subscribers;
            if (subscribers === 1) {
              const size = unsafeGet(this.pubsubSize, journal);
              const updatedNode = makeNode(undefined, 0, tail);
              unsafeSet(currentSubscriberHead, updatedNode, journal);
              unsafeSet(this.publisherHead, tail, journal);
              unsafeSet(this.pubsubSize, size - 1, journal);
            } else {
              const updatedNode = makeNode(head, subscribers - 1, tail);
              unsafeSet(currentSubscriberHead, updatedNode, journal);
            }
          }
          currentSubscriberHead = tail;
        }
      }
      const currentSubscriberCount = unsafeGet(this.subscriberCount, journal);
      unsafeSet(this.subscriberCount, currentSubscriberCount - 1, journal);
      unsafeSet(this.subscribers, remove(unsafeGet(this.subscribers, journal), this.subscriberHead), journal);
    }
  });
  take = withSTMRuntime(runtime => {
    let currentSubscriberHead = unsafeGet(this.subscriberHead, runtime.journal);
    if (currentSubscriberHead === undefined) {
      return interruptAs(runtime.fiberId);
    }
    let value = undefined;
    let loop = true;
    while (loop) {
      const node = unsafeGet(currentSubscriberHead, runtime.journal);
      if (node === undefined) {
        return retry;
      }
      const head = node.head;
      const tail = node.tail;
      if (head !== undefined) {
        const subscribers = node.subscribers;
        if (subscribers === 1) {
          const size = unsafeGet(this.pubsubSize, runtime.journal);
          const updatedNode = makeNode(void 0, 0, tail);
          unsafeSet(currentSubscriberHead, updatedNode, runtime.journal);
          unsafeSet(this.publisherHead, tail, runtime.journal);
          unsafeSet(this.pubsubSize, size - 1, runtime.journal);
        } else {
          const updatedNode = makeNode(head, subscribers - 1, tail);
          unsafeSet(currentSubscriberHead, updatedNode, runtime.journal);
        }
        unsafeSet(this.subscriberHead, tail, runtime.journal);
        value = head;
        loop = false;
      } else {
        currentSubscriberHead = tail;
      }
    }
    return succeed(value);
  });
  takeAll = this.takeUpTo(Number.POSITIVE_INFINITY);
  takeUpTo(max) {
    return withSTMRuntime(runtime => {
      let currentSubscriberHead = unsafeGet(this.subscriberHead, runtime.journal);
      if (currentSubscriberHead === undefined) {
        return interruptAs(runtime.fiberId);
      }
      const builder = [];
      let n = 0;
      while (n !== max) {
        const node = unsafeGet(currentSubscriberHead, runtime.journal);
        if (node === undefined) {
          n = max;
        } else {
          const head = node.head;
          const tail = node.tail;
          if (head !== undefined) {
            const subscribers = node.subscribers;
            if (subscribers === 1) {
              const size = unsafeGet(this.pubsubSize, runtime.journal);
              const updatedNode = makeNode(void 0, 0, tail);
              unsafeSet(currentSubscriberHead, updatedNode, runtime.journal);
              unsafeSet(this.publisherHead, tail, runtime.journal);
              unsafeSet(this.pubsubSize, size - 1, runtime.journal);
            } else {
              const updatedNode = makeNode(head, subscribers - 1, tail);
              unsafeSet(currentSubscriberHead, updatedNode, runtime.journal);
            }
            builder.push(head);
            n = n + 1;
          }
          currentSubscriberHead = tail;
        }
      }
      unsafeSet(this.subscriberHead, currentSubscriberHead, runtime.journal);
      return succeed(builder);
    });
  }
}

/** @internal */
const makeTPubSub = (requestedCapacity, strategy) => pipe(all([make(void 0), make(0)]), flatMap(([empty$1, pubsubSize]) => pipe(all([make(empty$1), make(empty$1), make(0), make(empty())]), map(([publisherHead, publisherTail, subscriberCount, subscribers]) => new TPubSubImpl(pubsubSize, publisherHead, publisherTail, requestedCapacity, strategy, subscriberCount, subscribers)))));
const makeSubscription = (pubsubSize, publisherHead, publisherTail, requestedCapacity, subscriberCount, subscribers) => pipe(get(publisherTail), flatMap(currentPublisherTail => pipe(all([make(currentPublisherTail), get(subscriberCount), get(subscribers)]), tap(([_, currentSubscriberCount]) => pipe(subscriberCount, set(currentSubscriberCount + 1))), tap(([subscriberHead, _, currentSubscribers]) => pipe(subscribers, set(pipe(currentSubscribers, add(subscriberHead))))), map(([subscriberHead]) => new TPubSubSubscriptionImpl(pubsubSize, publisherHead, requestedCapacity, subscriberHead, subscriberCount, subscribers)))));

/** @internal */
const awaitShutdown = self => self.awaitShutdown;

/** @internal */
const bounded = requestedCapacity => makeTPubSub(requestedCapacity, BackPressure);

/** @internal */
const capacity = self => self.capacity();

/** @internal */
const dropping = requestedCapacity => makeTPubSub(requestedCapacity, Dropping);

/** @internal */
const isEmpty = self => self.isEmpty;

/** @internal */
const isFull = self => self.isFull;

/** @internal */
const isShutdown = self => self.isShutdown;

/** @internal */
const publish = /*#__PURE__*/dual(2, (self, value) => self.offer(value));

/** @internal */
const publishAll = /*#__PURE__*/dual(2, (self, iterable) => self.offerAll(iterable));

/** @internal */
const size = self => self.size;

/** @internal */
const sliding = requestedCapacity => makeTPubSub(requestedCapacity, Sliding);

/** @internal */
const subscribe = self => makeSubscription(self.pubsubSize, self.publisherHead, self.publisherTail, self.requestedCapacity, self.subscriberCount, self.subscribers);

/** @internal */
const subscribeScoped = self => acquireRelease(subscribe(self), dequeue => shutdown(dequeue));

/** @internal */
const unbounded = () => makeTPubSub(Number.MAX_SAFE_INTEGER, Dropping);

export { TPubSubTypeId, awaitShutdown, bounded, capacity, dropping, isEmpty, isFull, isShutdown, makeNode, publish, publishAll, size, sliding, subscribe, subscribeScoped, unbounded };
